import { useState } from "react";
import { useNews } from "@/hooks/useNews";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StoriesManagementSection } from "@/components/admin/StoriesManagementSection";
import { EventRegistrationsSection } from "@/components/admin/EventRegistrationsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Eye, EyeOff, Calendar, Megaphone, Newspaper, Users, MapPin, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { FileUpload } from "@/components/ui/file-upload";
import { VenueLayoutEditor } from "@/components/admin/VenueLayoutEditor";

interface Speaker {
  id: string;
  name: string;
  image_url: string;
  position: string;
}

interface AgendaItem {
  id: string;
  time: string;
  title: string;
  description: string;
  speaker_ids: string[];
}

interface NewsFormData {
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  video_url: string;
  external_link: string;
  news_type: 'news' | 'event' | 'announcement';
  is_active: boolean;
  is_featured: boolean;
  published_at: string;
  // Event specific fields
  event_date: string;
  event_location: string;
  registration_deadline: string;
  registration_link: string;
  has_registration: boolean;
  max_capacity: number | null;
  registration_enabled: boolean;
  venue_layout: any;
  venue_capacity_by_row: any;
  speakers: Speaker[];
  agenda: AgendaItem[];
  // Announcement specific fields
  valid_until: string;
  is_urgent: boolean;
  // Common fields
  tags: string;
}

const initialFormData: NewsFormData = {
  title: '',
  content: '',
  excerpt: '',
  image_url: '',
  video_url: '',
  external_link: '',
  news_type: 'news',
  is_active: true,
  is_featured: false,
  published_at: '',
  // Event specific fields
  event_date: '',
  event_location: '',
  registration_deadline: '',
  registration_link: '',
  has_registration: false,
  max_capacity: null,
  registration_enabled: true,
  venue_layout: null,
  venue_capacity_by_row: null,
  speakers: [],
  agenda: [],
  // Announcement specific fields
  valid_until: '',
  is_urgent: false,
  // Common fields
  tags: ''
};

const getNewsTypeIcon = (type: string) => {
  switch (type) {
    case 'event':
      return <Calendar className="h-4 w-4" />;
    case 'announcement':
      return <Megaphone className="h-4 w-4" />;
    default:
      return <Newspaper className="h-4 w-4" />;
  }
};

const getNewsTypeLabel = (type: string) => {
  switch (type) {
    case 'event':
      return 'Միջոցառում';
    case 'announcement':
      return 'Հայտարարություն';
    default:
      return 'Նորություն';
  }
};

export const NewsSection = () => {
  const { news, loading, createNews, updateNews, deleteNews } = useNews(true); // Enable admin mode
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<any>(null);
  const [formData, setFormData] = useState<NewsFormData>(initialFormData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const newsData = {
        ...formData,
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : null,
        event_date: formData.event_date ? new Date(formData.event_date).toISOString() : null,
        registration_deadline: formData.registration_deadline ? new Date(formData.registration_deadline).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };

      if (editingNews) {
        await updateNews(editingNews.id, newsData);
        toast.success("Նորությունը հաջողությամբ թարմացվեց");
      } else {
        await createNews(newsData);
        toast.success("Նորությունը հաջողությամբ ավելացվեց");
      }
      
      setIsDialogOpen(false);
      setEditingNews(null);
      setFormData(initialFormData);
    } catch (error) {
      toast.error("Սխալ է տեղի ունեցել");
    }
  };

  const handleEdit = (newsItem: any) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title || '',
      content: newsItem.content || '',
      excerpt: newsItem.excerpt || '',
      image_url: newsItem.image_url || '',
      video_url: newsItem.video_url || '',
      external_link: newsItem.external_link || '',
      news_type: newsItem.news_type || 'news',
      is_active: newsItem.is_active ?? true,
      is_featured: newsItem.is_featured ?? false,
      published_at: newsItem.published_at ? new Date(newsItem.published_at).toISOString().split('T')[0] : '',
      // Event specific fields
      event_date: newsItem.event_date ? new Date(newsItem.event_date).toISOString().slice(0, 16) : '',
      event_location: newsItem.event_location || '',
      registration_deadline: newsItem.registration_deadline ? new Date(newsItem.registration_deadline).toISOString().split('T')[0] : '',
      registration_link: newsItem.registration_link || '',
      has_registration: newsItem.has_registration ?? false,
      max_capacity: newsItem.max_capacity,
      registration_enabled: newsItem.registration_enabled ?? true,
      venue_layout: newsItem.venue_layout || null,
      venue_capacity_by_row: newsItem.venue_capacity_by_row || null,
      speakers: newsItem.speakers || [],
      agenda: newsItem.agenda || [],
      // Announcement specific fields
      valid_until: newsItem.valid_until || '',
      is_urgent: newsItem.is_urgent ?? false,
      // Common fields
      tags: newsItem.tags ? newsItem.tags.join(', ') : ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Վստա՞հ եք, որ ցանկանում եք ջնջել այս նորությունը:")) {
      try {
        await deleteNews(id);
        toast.success("Նորությունը հաջողությամբ ջնջվեց");
      } catch (error) {
        toast.error("Ջնջելու ժամանակ սխալ է տեղի ունեցել");
      }
    }
  };

  const toggleActive = async (newsItem: any) => {
    try {
      await updateNews(newsItem.id, { ...newsItem, is_active: !newsItem.is_active });
      toast.success(`Նորությունը ${newsItem.is_active ? 'անջատվեց' : 'միացվեց'}`);
    } catch (error) {
      toast.error("Կարգավիճակը փոխելու ժամանակ սխալ է տեղի ունեցել");
    }
  };

  return (
    <Tabs defaultValue="news" className="w-full">
      <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <TabsList className="inline-flex h-auto min-w-max gap-1 border border-border/70 bg-muted/70 p-1.5 sm:grid sm:w-full sm:min-w-0 sm:grid-cols-3">
          <TabsTrigger value="news" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:text-sm">Նորություններ</TabsTrigger>
          <TabsTrigger value="registrations" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:text-sm">Գրանցումներ</TabsTrigger>
          <TabsTrigger value="stories" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:text-sm">Պատմություններ</TabsTrigger>
        </TabsList>
      </div>
      
      <TabsContent value="news">
        <Card>
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Նորություններ</CardTitle>
            <CardDescription>
              Կառավարեք կայքի նորությունները, միջոցառումները և հայտարարությունները
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingNews(null);
                setFormData(initialFormData);
              }} className="mb-4 min-h-11 w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Ավելացնել նորություն
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingNews ? 'Խմբագրել նորությունը' : 'Ավելացնել նորություն'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="title">Վերնագիր *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="news_type">Տեսակ</Label>
                    <Select value={formData.news_type} onValueChange={(value: any) => setFormData({ ...formData, news_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="news">Նորություն</SelectItem>
                        <SelectItem value="event">Միջոցառում</SelectItem>
                        <SelectItem value="announcement">Հայտարարություն</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="excerpt">Կարճ նկարագրություն</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="content">Բովանդակություն</Label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Գրեք նորության բովանդակությունը... Օգտագործեք Markdown ֆորմատավորում՝ **թավ**, *շեղ*, `կոդ`, ## վերնագրեր, հղումներ [տեքստ](URL), նկարներ ![նկար](URL), աղյուսակներ և ավելին:"
                    height={400}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FileUpload
                    accept="image/*"
                    bucketName="news-media"
                    onUploadComplete={(url) => setFormData({ ...formData, image_url: url })}
                    label="Նկար"
                    currentUrl={formData.image_url}
                    onUrlChange={(url) => setFormData({ ...formData, image_url: url })}
                    placeholder="https://example.com/image.jpg"
                    maxSize={5}
                  />
                  <FileUpload
                    accept="video/*"
                    bucketName="news-media"
                    onUploadComplete={(url) => setFormData({ ...formData, video_url: url })}
                    label="Տեսանյութ"
                    currentUrl={formData.video_url}
                    onUrlChange={(url) => setFormData({ ...formData, video_url: url })}
                    placeholder="https://example.com/video.mp4"
                    maxSize={50}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="external_link">Արտաքին հղում</Label>
                    <Input
                      id="external_link"
                      type="url"
                      value={formData.external_link}
                      onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="published_at">Հրապարակման ամսաթիվ</Label>
                    <Input
                      id="published_at"
                      type="date"
                      value={formData.published_at}
                      onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="tags">Թեգեր (ստորակետով բաժանված)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="տեխնոլոգիա, կրթություն, IT"
                  />
                </div>

                {/* Event specific fields */}
                {formData.news_type === 'event' && (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="event_date">🗓 Միջոցառման ամսաթիվ և ժամ</Label>
                        <Input
                          id="event_date"
                          type="datetime-local"
                          value={formData.event_date}
                          onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="event_location">📍 Վայր</Label>
                        <Input
                          id="event_location"
                          value={formData.event_location}
                          onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                          placeholder="Երևան, ԱՄՆ փողոց 1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="registration_deadline">⏳ Մասնակցության վերջնաժամկետ</Label>
                        <Input
                          id="registration_deadline"
                          type="date"
                          value={formData.registration_deadline}
                          onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                        />
                      </div>
                       <div>
                         <Label htmlFor="has_registration" className="flex items-center gap-2">
                           <input
                             id="has_registration"
                             type="checkbox"
                             checked={formData.has_registration || false}
                             onChange={(e) => setFormData({ ...formData, has_registration: e.target.checked })}
                             className="rounded"
                           />
                           🎫 Ունի ներկառուցված գրանցման ֆորմա
                         </Label>
                       </div>
                    </div>
                    
                    {formData.has_registration && (
                      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                        <h4 className="font-medium">Գրանցման կարգավորումներ</h4>
                        
                        <div className="flex items-center gap-2">
                          <input
                            id="registration_enabled"
                            type="checkbox"
                            checked={formData.registration_enabled}
                            onChange={(e) => setFormData({ ...formData, registration_enabled: e.target.checked })}
                            className="rounded"
                          />
                          <Label htmlFor="registration_enabled">Գրանցումը բաց է</Label>
                        </div>

                        <div>
                          <Label htmlFor="max_capacity">Առավելագույն տեղեր (ոչ պարտադիր)</Label>
                          <Input
                            id="max_capacity"
                            type="number"
                            min="1"
                            placeholder="Մուտքագրեք առավելագույն մասնակիցների քանակը"
                            value={formData.max_capacity || ''}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              max_capacity: e.target.value ? parseInt(e.target.value) : null 
                            })}
                          />
                        </div>
                      </div>
                    )}
                    
                    {!formData.has_registration && (
                      <div>
                        <Label htmlFor="registration_link">🔗 Գրանցման հղում</Label>
                        <Input
                          id="registration_link"
                          type="url"
                          value={formData.registration_link}
                          onChange={(e) => setFormData({ ...formData, registration_link: e.target.value })}
                          placeholder="https://example.com/register"
                        />
                      </div>
                    )}

                    {/* Venue Layout Configuration */}
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/10">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <h4 className="font-medium">Դահլիճի կարգավորում</h4>
                      </div>
                      <VenueLayoutEditor
                        value={formData.venue_layout}
                        onChange={(layout) => {
                          setFormData({
                            ...formData,
                            venue_layout: layout,
                            venue_capacity_by_row: layout.sections.reduce((acc, section) => {
                              section.rows.forEach((row) => {
                                acc[`${section.id}-${row.id}`] = {
                                  section: section.name,
                                  row: row.rowNumber,
                                  seats: row.seatCount,
                                  price: section.price
                                };
                              });
                              return acc;
                            }, {} as any),
                            max_capacity: layout.totalCapacity
                          });
                        }}
                      />
                    </div>

                    {/* Speakers Section */}
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/10">
                       <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <h4 className="font-medium">Բանախոսներ</h4>
                        </div>
                         <Button
                          type="button"
                          size="sm"
                           variant="outline"
                           className="min-h-11 w-full sm:w-auto"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              speakers: [
                                ...formData.speakers,
                                {
                                  id: crypto.randomUUID(),
                                  name: '',
                                  image_url: '',
                                  position: ''
                                }
                              ]
                            });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ավելացնել բանախոս
                        </Button>
                      </div>
                      
                      {formData.speakers.length > 0 && (
                        <div className="space-y-4">
                          {formData.speakers.map((speaker, index) => (
                            <Card key={speaker.id} className="p-4">
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <h5 className="font-medium text-sm">Բանախոս #{index + 1}</h5>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        speakers: formData.speakers.filter((_, i) => i !== index)
                                      });
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <div>
                                  <Label>Անուն Ազգանուն</Label>
                                  <Input
                                    value={speaker.name}
                                    onChange={(e) => {
                                      const newSpeakers = [...formData.speakers];
                                      newSpeakers[index] = { ...speaker, name: e.target.value };
                                      setFormData({ ...formData, speakers: newSpeakers });
                                    }}
                                    placeholder="Արամ Սարգսյան"
                                  />
                                </div>
                                
                                <div>
                                  <Label>Մասնագիտություն / Պաշտոն</Label>
                                  <Input
                                    value={speaker.position}
                                    onChange={(e) => {
                                      const newSpeakers = [...formData.speakers];
                                      newSpeakers[index] = { ...speaker, position: e.target.value };
                                      setFormData({ ...formData, speakers: newSpeakers });
                                    }}
                                    placeholder="Կիբերանվտանգության փորձագետ"
                                  />
                                </div>
                                
                                <div>
                                  <Label>Նկար URL</Label>
                                  <Input
                                    value={speaker.image_url}
                                    onChange={(e) => {
                                      const newSpeakers = [...formData.speakers];
                                      newSpeakers[index] = { ...speaker, image_url: e.target.value };
                                      setFormData({ ...formData, speakers: newSpeakers });
                                    }}
                                    placeholder="https://example.com/speaker.jpg"
                                  />
                                </div>
                                
                                {speaker.image_url && (
                                  <div className="flex justify-center">
                                    <img
                                      src={speaker.image_url}
                                      alt={speaker.name}
                                      className="w-24 h-24 object-cover rounded-full border-2 border-border"
                                    />
                                  </div>
                                )}
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {formData.speakers.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Դեռ չեք ավելացրել բանախոսներ
                        </p>
                      )}
                    </div>

                    {/* Agenda Section */}
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/10">
                       <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <h4 className="font-medium">Օրակարգ</h4>
                        </div>
                         <Button
                          type="button"
                          size="sm"
                           variant="outline"
                           className="min-h-11 w-full sm:w-auto"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              agenda: [
                                ...formData.agenda,
                                {
                                  id: crypto.randomUUID(),
                                  time: '',
                                  title: '',
                                  description: '',
                                  speaker_ids: []
                                }
                              ]
                            });
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ավելացնել օրակարգի կետ
                        </Button>
                      </div>
                      
                      {formData.agenda.length > 0 && (
                        <div className="space-y-4">
                          {formData.agenda.map((item, index) => (
                            <Card key={item.id} className="p-4 bg-background">
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <h5 className="font-medium text-sm">Կետ #{index + 1}</h5>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        agenda: formData.agenda.filter((_, i) => i !== index)
                                      });
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <div>
                                  <Label>Ժամ</Label>
                                  <Input
                                    value={item.time}
                                    onChange={(e) => {
                                      const newAgenda = [...formData.agenda];
                                      newAgenda[index] = { ...item, time: e.target.value };
                                      setFormData({ ...formData, agenda: newAgenda });
                                    }}
                                    placeholder="10:00 - 11:00"
                                  />
                                </div>
                                
                                <div>
                                  <Label>Վերնագիր</Label>
                                  <Input
                                    value={item.title}
                                    onChange={(e) => {
                                      const newAgenda = [...formData.agenda];
                                      newAgenda[index] = { ...item, title: e.target.value };
                                      setFormData({ ...formData, agenda: newAgenda });
                                    }}
                                    placeholder="Բացման խոսք"
                                  />
                                </div>
                                
                                <div>
                                  <Label>Նկարագրություն</Label>
                                  <Textarea
                                    value={item.description}
                                    onChange={(e) => {
                                      const newAgenda = [...formData.agenda];
                                      newAgenda[index] = { ...item, description: e.target.value };
                                      setFormData({ ...formData, agenda: newAgenda });
                                    }}
                                    placeholder="Մանրամասներ օրակարգի կետի մասին"
                                    rows={2}
                                  />
                                </div>
                                
                                {/* Speaker Selection */}
                                {formData.speakers.length > 0 && (
                                  <div>
                                    <Label>Բանախոսներ</Label>
                                    <div className="space-y-2 mt-2">
                                      {formData.speakers.map((speaker) => (
                                        <div key={speaker.id} className="flex items-center gap-2">
                                          <input
                                            type="checkbox"
                                            id={`agenda-${item.id}-speaker-${speaker.id}`}
                                            checked={item.speaker_ids?.includes(speaker.id) || false}
                                            onChange={(e) => {
                                              const newAgenda = [...formData.agenda];
                                              const currentSpeakerIds = item.speaker_ids || [];
                                              if (e.target.checked) {
                                                newAgenda[index] = { 
                                                  ...item, 
                                                  speaker_ids: [...currentSpeakerIds, speaker.id] 
                                                };
                                              } else {
                                                newAgenda[index] = { 
                                                  ...item, 
                                                  speaker_ids: currentSpeakerIds.filter(id => id !== speaker.id) 
                                                };
                                              }
                                              setFormData({ ...formData, agenda: newAgenda });
                                            }}
                                            className="rounded"
                                          />
                                          <Label 
                                            htmlFor={`agenda-${item.id}-speaker-${speaker.id}`}
                                            className="cursor-pointer text-sm flex items-center gap-2"
                                          >
                                            {speaker.image_url && (
                                              <img 
                                                src={speaker.image_url} 
                                                alt={speaker.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                              />
                                            )}
                                            <span>{speaker.name}</span>
                                            {speaker.position && (
                                              <span className="text-xs text-muted-foreground">({speaker.position})</span>
                                            )}
                                          </Label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {formData.speakers.length === 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    Նախ ավելացրեք բանախոսներ՝ նրանց կցելու համար օրակարգին
                                  </p>
                                )}
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {formData.agenda.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Դեռ չեք ավելացրել օրակարգի կետեր
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Announcement specific fields */}
                {formData.news_type === 'announcement' && (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="valid_until">📌 Վավերության ժամկետ</Label>
                        <Input
                          id="valid_until"
                          type="date"
                          value={formData.valid_until}
                          onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center space-x-2 mt-6">
                        <Switch
                          id="is_urgent"
                          checked={formData.is_urgent}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_urgent: checked })}
                        />
                        <Label htmlFor="is_urgent">⚡ Շտապ հայտարարություն</Label>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label htmlFor="is_active">Ակտիվ</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                    />
                    <Label htmlFor="is_featured">Առաջատար</Label>
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="outline" className="min-h-11 w-full sm:w-auto" onClick={() => setIsDialogOpen(false)}>
                    Չեղարկել
                  </Button>
                  <Button type="submit" className="min-h-11 w-full sm:w-auto">
                    {editingNews ? 'Թարմացնել' : 'Ավելացնել'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        {loading ? (
          <div>Բեռնում...</div>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:thin] sm:mx-0 sm:px-0">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Վերնագիր</TableHead>
                <TableHead>Տեսակ</TableHead>
                <TableHead>Հրապարակման ամսաթիվ</TableHead>
                <TableHead>Կարգավիճակ</TableHead>
                <TableHead>Գործողություններ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {news.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {getNewsTypeIcon(item.news_type)}
                      <span className="font-medium">{item.title}</span>
                      {item.is_featured && <Badge variant="default">Ընտրված</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="secondary">
                        {getNewsTypeLabel(item.news_type)}
                      </Badge>
                      {item.news_type === 'event' && (item as any).has_registration !== undefined && (
                        <>
                          <Badge variant={(item as any).has_registration ? "default" : "secondary"}>
                            {(item as any).has_registration ? "Ունի գրանցում" : "Չունի գրանցում"}
                          </Badge>
                          {(item as any).has_registration && (item as any).registration_enabled !== undefined && (
                            <>
                              <Badge variant={(item as any).registration_enabled ? "default" : "destructive"}>
                                {(item as any).registration_enabled ? "Գրանցումը բաց է" : "Գրանցումը փակ է"}
                              </Badge>
                              {(item as any).max_capacity && (
                                <Badge variant="outline">
                                  <Users className="h-3 w-3 mr-1" />
                                  Մաքս․ {(item as any).max_capacity} տեղ
                                </Badge>
                              )}
                            </>
                          )}
                          {(item as any).venue_layout && (
                            <Badge variant="outline" className="bg-primary/10">
                              <MapPin className="h-3 w-3 mr-1" />
                              Դահլիճ կարգավորված
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.published_at
                      ? format(new Date(item.published_at), 'dd/MM/yyyy')
                      : 'Չհրապարակված'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? 'default' : 'secondary'}>
                      {item.is_active ? 'Ակտիվ' : 'Ապաակտիվ'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(item)}
                      >
                        {item.is_active ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  </TabsContent>
  
  <TabsContent value="registrations">
    <EventRegistrationsSection />
  </TabsContent>
  
  <TabsContent value="stories">
    <StoriesManagementSection />
  </TabsContent>
</Tabs>
  );
};